/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import {
  fetchSessionDetails,
  fetchSummaryDetails,
  fetchUrlSessionDetails,
  fetchScreenshots,
  fetchDeviceStatus,
  fetchFaceDetection,
  ScreenshotDataset,
  UrlSessionDataset,
  SessionDataset,
  SummaryDataset,
  DeviceStatusDataset,
  FaceDetectionDataset,
  FilterParams,
  fetchTrackingUserSettings,
} from "./WorkDetails";
import { colors } from "themes";
import MediaPreview, { useMediaPreview, MediaItem } from "components/MediaPreview";
import { getImageUrl } from "util/googleDriveHelper";
import Sessions from "pages/tracking/Sessions";
import UrlSessions from "pages/tracking/UrlSessions";
import DeviceStatus from "pages/tracking/DeviceStatus";
import FaceDetection from "pages/tracking/FaceDetection";
import CommonListingFilter, {
  CommonFilterField,
} from "components/CommonListingFilter/CommonListingFilter";
import EmployeeLiveMonitor from "pages/tracking/EmployeeLiveMonitor";
import UserSettings from "./UserSettings";
import Timeline from "components/Timeline";

/* ────────────────────────────────────────────────────────────
   HELPERS & ICONS FOR SUMMARY VIEW
──────────────────────────────────────────────────────────── */
const formatDuration = (secs: number | null | undefined) => {
  if (!secs || secs <= 0) return "0m";
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const appDisplayName = (exeName: string) => exeName.replace(/\.exe$/i, "");

const APP_LOGO_MAP = [
  { test: /code|vscode/i, style: { bg: "#e0edff", fg: "#2563eb", mark: "VS" } },
  { test: /chrome/i, style: { bg: "#fef2e0", fg: "#ea8600", mark: "Ch" } },
  { test: /firefox/i, style: { bg: "#ffe8e0", fg: "#ff6611", mark: "Fx" } },
  { test: /edge/i, style: { bg: "#e0f7ee", fg: "#0d9d58", mark: "Ed" } },
  { test: /teams/i, style: { bg: "#e5e0ff", fg: "#5b5fc7", mark: "Tm" } },
  { test: /slack/i, style: { bg: "#f3e0ff", fg: "#8b2f8f", mark: "Sl" } },
  { test: /photoshop|^ps/i, style: { bg: "#dbe9ff", fg: "#1e3a8a", mark: "Ps" } },
  { test: /illustrator/i, style: { bg: "#fde9d7", fg: "#c2410c", mark: "Ai" } },
  { test: /figma/i, style: { bg: "#f2e0ff", fg: "#7c3aed", mark: "Fi" } },
  { test: /idea|intellij/i, style: { bg: "#ffe6e6", fg: "#dc2626", mark: "IJ" } },
  { test: /terminal|powershell|cmd/i, style: { bg: "#e5e5e5", fg: "#374151", mark: ">_" } },
  { test: /outlook/i, style: { bg: "#dbeafe", fg: "#1d4ed8", mark: "Ou" } },
  { test: /excel/i, style: { bg: "#dcfce7", fg: "#15803d", mark: "Xl" } },
  { test: /word/i, style: { bg: "#dbeafe", fg: "#1d4ed8", mark: "Wd" } },
  { test: /zoom/i, style: { bg: "#dbeafe", fg: "#2563eb", mark: "Zm" } },
  { test: /notion/i, style: { bg: "#e5e5e5", fg: "#111827", mark: "No" } },
  { test: /explorer/i, style: { bg: "#fef3c7", fg: "#b45309", mark: "Ex" } },
];

const getAppLogoStyle = (exeName: string) => {
  const found = APP_LOGO_MAP.find((entry) => entry.test.test(exeName));
  if (found) return found.style;
  const clean = exeName.replace(/\.exe$/i, "");
  return { bg: "#dbeafe", fg: "#2563eb", mark: clean.slice(0, 2).toUpperCase() };
};

const getUrlLogoStyle = (domain: string) => {
  const clean = domain.replace(/^www\./i, "");
  let mark = clean.slice(0, 2).toUpperCase();

  if (clean.includes("onedrive")) mark = "OD";
  if (clean.includes("localhost")) mark = "LH";
  if (clean.includes("lirisoft") || clean.includes("liriworks")) mark = "LW";

  return { bg: "#e0f2fe", fg: "#0ea5e9", mark };
};

const ClockIcon = () => <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const TrendIcon = () => <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>;
const CalendarIcon = () => <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>;
const CoffeeIcon = () => <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 8h1a4 4 0 010 8h-1M18 8H4a4 4 0 004 4v1a4 4 0 004 4h2a4 4 0 004-4v-1a4 4 0 00-4-4z" /></svg>;
const LeaveIcon = () => <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
const AssetIcon = () => <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>;
const BulbIcon = () => <svg className="w-[16px] h-[16px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.472V16a1 1 0 001 1h6a1 1 0 001-1v-2.528A6 6 0 0012 3z" /></svg>;
const InfoIcon = () => <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const AwardIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15a4 4 0 100-8 4 4 0 000 8zM6 20l1.5-5M18 20l-1.5-5" /></svg>;
const StarIcon = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.977 2.89a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.89a1 1 0 00-1.176 0l-3.976 2.89c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.977-2.89c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>;
const GlobeIcon = () => <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
const ExternalLinkIcon = () => <svg className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>;

const StatCard = ({ icon, iconBg, iconColor, label, value }: { icon: React.ReactNode; iconBg: string; iconColor: string; label: string; value: React.ReactNode; }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm px-3 py-3 flex items-center gap-2.5">
    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: iconBg, color: iconColor }}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-[12px] font-medium text-gray-500 dark:text-gray-400 leading-tight truncate">{label}</p>
      <p className="text-[18px] font-bold text-gray-900 dark:text-white leading-tight truncate">{value}</p>
    </div>
  </div>
);

/* ────────────────────────────────────────────────────────────
   TYPES
──────────────────────────────────────────────────────────── */
interface FilterValues {
  fromDate: string;
  toDate: string;
  status: string;
  user: string;
}

interface FilterErrors {
  fromDate?: string;
  toDate?: string;
}

interface WorkSummaryProps {
  employeeId: string | number;
  employeeName: string;
  employeeEmail: string;
  hideNavigation?: boolean;
  initialView?: SummaryView;
  managerJwt: string;
}

type SummaryView =
  | "summary"
  | "sessions"
  | "urlSessions"
  | "screenshots"
  | "deviceStatus"
  | "faceRecognition"
  | "liveStream"
  | "userSettings";

// Reusable Banner Component
export const TrackingDisabledBanner = ({ message }: { message: string }) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  return (
    <div className="flex-1 flex justify-center">
      <div className="flex items-center justify-between gap-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-4 py-1.5 rounded-lg text-sm font-medium border border-red-200 dark:border-red-800/30">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          {message}
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="text-red-400 hover:text-red-600 dark:text-red-500 dark:hover:text-red-300 transition-colors focus:outline-none flex-shrink-0 ml-2 p-0.5 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50"
          aria-label="Dismiss"
          title="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const WorkSummary: React.FC<WorkSummaryProps> = ({
  employeeEmail,
  hideNavigation = false,
  initialView = "summary",
  employeeId,
  managerJwt,
}) => {
  const [features, setFeatures] = useState<any>({});
  const [activeView, setActiveView] = useState<SummaryView>(initialView);
  const [summaryDataset, setSummaryDataset] = useState<SummaryDataset | any>(null);
  const [sessionDataset, setSessionDataset] = useState<SessionDataset[]>([]);
  const [urlSessionDataset, setUrlSessionDataset] = useState<UrlSessionDataset[]>([]);
  const [screenshotsDataset, setScreenshotsDataset] = useState<ScreenshotDataset[]>([]);
  const [deviceStatusDataset, setDeviceStatusDataset] = useState<DeviceStatusDataset | null>(null);
  const [faceDetectionDataset, setFaceDetectionDataset] = useState<FaceDetectionDataset | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterValues>({
    fromDate: "",
    toDate: "",
    status: "all",
    user: "",
  });
  const [localFilters, setLocalFilters] = useState<FilterValues>({
    fromDate: "",
    toDate: "",
    status: "all",
    user: "",
  });
  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});

  const [sessionsPagination, setSessionsPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
  });
  const [urlSessionsPagination, setUrlSessionsPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalElements: 0,
  });
  const [screenshotsPagination, setScreenshotsPagination] = useState({
    currentPage: 1,
    pageSize: 12,
    totalPages: 0,
    totalElements: 0,
  });
  const [faceDetectionPagination, setFaceDetectionPagination] = useState({
    currentPage: 1,
    pageSize: 100,
    totalPages: 0,
    totalElements: 0,
  });

  const { previewProps, open: openScreenshotPreview } = useMediaPreview();

  useEffect(() => {
    setActiveView(initialView);
  }, [initialView]);

  const handleApplyFilter = (newFilters: FilterValues) => {
    if (JSON.stringify(newFilters) === JSON.stringify(filters)) return;
    setFilters(newFilters);
  };

  const updateLocalFilter = (key: keyof FilterValues, value: string) => {
    setLocalFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "fromDate" && !value) next.toDate = "";
      return next;
    });
    if (key === "fromDate" || key === "toDate") {
      setFilterErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const handleApplyFilters = () => {
    const errors: FilterErrors = {};
    let hasError = false;
    if (!localFilters.fromDate) { errors.fromDate = "Start Date is required"; hasError = true; }
    if (!localFilters.toDate) { errors.toDate = "End Date is required"; hasError = true; }
    setFilterErrors(errors);
    if (hasError) return false;
    handleApplyFilter(localFilters);
    return true;
  };

  const handleClearFilters = () => {
    setLocalFilters((prev) => ({ ...prev, fromDate: "", toDate: "" }));
    setFilterErrors({});
  };

  const handleResetFilters = () => {
    const reset = { fromDate: "", toDate: "", status: "all", user: "" };
    setLocalFilters(reset);
    setFilterErrors({});
    handleApplyFilter(reset);
  };

  const handleCloseFilter = () => setFilterErrors({});
  const todayStr = new Date().toISOString().split("T")[0];
  const workSummaryFilterFields: CommonFilterField[] = [
    {
      id: "work-summary-from-date",
      type: "date",
      label: "From Date",
      widthClass: "min-w-[170px]",
      queryParam: "fromDate",
      value: localFilters.fromDate,
      max: localFilters.toDate || todayStr,
      onChange: (value) => updateLocalFilter("fromDate", value),
      isRequired: true,
      errorMessage: filterErrors.fromDate,
    },
    {
      id: "work-summary-to-date",
      max: todayStr,
      type: "date",
      label: "To Date",
      widthClass: "min-w-[170px]",
      queryParam: "toDate",
      value: localFilters.toDate,
      min: localFilters.fromDate || undefined,
      onChange: (value) => updateLocalFilter("toDate", value),
      isRequired: true,
      errorMessage: filterErrors.toDate,
    },
  ];

  const formatDateForAPI = (dateString: string | undefined): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    return `${day}-${month}-${year}`;
  };

  const getAPIFilters = (page?: number, pageSize?: number): FilterParams => ({
    fromDate: formatDateForAPI(filters.fromDate),
    toDate: formatDateForAPI(filters.toDate),
    status: filters.status,
    page,
    pageSize,
  });

  const handleSessionsPageChange = (page: number, pageSize: number) => {
    setSessionsPagination((prev) => ({ ...prev, currentPage: page, pageSize }));
  };

  const handleUrlSessionsPageChange = (page: number, pageSize: number) => {
    setUrlSessionsPagination((prev) => ({ ...prev, currentPage: page, pageSize }));
  };

  const handleScreenshotsPageChange = (page: number, pageSize: number) => {
    setScreenshotsPagination((prev) => ({ ...prev, currentPage: page, pageSize }));
  };

  const updatePagination = (
    setter: React.Dispatch<React.SetStateAction<{ currentPage: number; pageSize: number; totalPages: number; totalElements: number }>>,
    data: { totalPages: number; totalElements: number; currentPage: number },
  ) => {
    setter((prev) => ({
      ...prev,
      totalPages: data.totalPages,
      totalElements: data.totalElements,
      currentPage: data.currentPage,
    }));
  };

  const mapScreenshotData = (item: any) => ({
    ...item,
    imageUrl: item.trackingImageUrl || item.imageUrl,
    timestamp: item.createdAt,
    filename: item.fileName,
  });

  const handleSummaryFetch = async () => {
    const data = await fetchSummaryDetails(employeeId, getAPIFilters());
    if (data) setSummaryDataset(data);
  };

  const handleSessionsFetch = async () => {
    const data = await fetchSessionDetails(employeeId, getAPIFilters(sessionsPagination.currentPage, sessionsPagination.pageSize));
    if (data) { setSessionDataset(data.contents); updatePagination(setSessionsPagination, data); }
  };

  const handleUrlSessionsFetch = async () => {
    const data = await fetchUrlSessionDetails(employeeId, getAPIFilters(urlSessionsPagination.currentPage, urlSessionsPagination.pageSize));
    if (data) { setUrlSessionDataset(data.contents); updatePagination(setUrlSessionsPagination, data); }
  };

  const handleScreenshotsFetch = async () => {
    const data = await fetchScreenshots(employeeId, getAPIFilters(screenshotsPagination.currentPage, screenshotsPagination.pageSize));
    if (Array.isArray(data)) { setScreenshotsDataset(data.map(mapScreenshotData)); return; }
    if (data && Array.isArray(data.contents)) {
      setScreenshotsDataset(data.contents.map(mapScreenshotData));
      updatePagination(setScreenshotsPagination, data);
      return;
    }
    setScreenshotsDataset([]);
  };

  const handleDeviceStatusFetch = async () => {
    setDeviceStatusDataset(null);
    const data = await fetchDeviceStatus(employeeId);
    if (data) setDeviceStatusDataset(data);
  };

  const handleFaceRecognitionFetch = async () => {
    setFaceDetectionDataset(null);
    const data = await fetchFaceDetection(employeeId, getAPIFilters(faceDetectionPagination.currentPage, faceDetectionPagination.pageSize));
    if (data) { setFaceDetectionDataset(data); updatePagination(setFaceDetectionPagination, data); }
  };

  useEffect(() => { setLocalFilters(filters); }, [filters]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        switch (activeView) {
          case "summary": await handleSummaryFetch(); break;
          case "sessions": await handleSessionsFetch(); break;
          case "urlSessions": await handleUrlSessionsFetch(); break;
          case "screenshots": await handleScreenshotsFetch(); break;
          case "deviceStatus": await handleDeviceStatusFetch(); break;
          case "faceRecognition": await handleFaceRecognitionFetch(); break;
          case "liveStream": break;
          default: break;
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (activeView !== "userSettings") {
      fetchData();
    }
  }, [
    activeView, employeeId, filters,
    sessionsPagination.currentPage, sessionsPagination.pageSize,
    urlSessionsPagination.currentPage, urlSessionsPagination.pageSize,
    screenshotsPagination.currentPage, screenshotsPagination.pageSize,
    faceDetectionPagination.currentPage, faceDetectionPagination.pageSize,
  ]);

  useEffect(() => {
    const fetchSettings = async () => {
      const data = await fetchTrackingUserSettings(employeeId);
      if (data) {
        setFeatures(data);
      }
    };

    fetchSettings();

    const handleFeaturesChange = () => {
      fetchSettings();
    };

    window.addEventListener("featuresEnabledChanged", handleFeaturesChange);
    return () => window.removeEventListener("featuresEnabledChanged", handleFeaturesChange);
  }, [employeeId, activeView]);

  const menuItems: Array<{ key: SummaryView; label: string; icon: JSX.Element }> = [
    {
      key: "summary",
      label: "Summary",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    {
      key: "sessions",
      label: "Sessions",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: "urlSessions",
      label: "URLs",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        </svg>
      ),
    },
    {
      key: "screenshots",
      label: "Screenshots",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      key: "deviceStatus",
      label: "Device",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      key: "faceRecognition",
      label: "Face ID",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: "liveStream",
      label: "Live",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      key: "userSettings",
      label: "User Settings",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  const renderContent = () => {
    if (activeView === "userSettings") {
      return <UserSettings employeeId={employeeId} />;
    }

    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-7 h-7 border-2 border-blue-600 dark:border-blue-400 border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading data...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-10 h-10 text-red-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ backgroundColor: colors.primary }}
            className="px-4 py-1.5 text-white text-sm rounded-md hover:opacity-90"
          >
            Retry
          </button>
        </div>
      );
    }

    switch (activeView) {
      case "summary": {
        if (!summaryDataset) {
          return <p className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No summary data available. Select a date range and apply filters.</p>;
        }

        const donutSlices = [
          { label: "Focused Time", value: formatDuration(summaryDataset.activeTime), percent: summaryDataset.activePercentage, color: colors.primary },
          { label: "Break Time", value: formatDuration(summaryDataset.breakTime), percent: summaryDataset.breakPercentage, color: "#60a5fa" },
          { label: "Idle Time", value: formatDuration(summaryDataset.idleTime), percent: summaryDataset.idlePercentage, color: "#d1d5db" },
        ];

        const svgSize = 112;
        const strokeWidth = 11;
        const radius = (svgSize - strokeWidth) / 2;
        const circumference = 2 * Math.PI * radius;
        const gapInPixels = 3;
        const validSlices = donutSlices.filter((s) => s.percent > 0);
        let currentOffset = 0;

        return (
          <div className="space-y-4 px-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
              <StatCard
                icon={<ClockIcon />}
                iconBg="#eff6ff"
                iconColor={colors.primary}
                label="Total Work Time"
                value={formatDuration(summaryDataset.totalTime)}
              />
              <StatCard
                icon={<TrendIcon />}
                iconBg="#eff6ff"
                iconColor={colors.primary}
                label="Productivity"
                value={`${summaryDataset.activePercentage?.toFixed(1) || 0}%`}
              />
              <StatCard
                icon={<CalendarIcon />}
                iconBg="#eff6ff"
                iconColor={colors.primary}
                label="Sessions"
                value={summaryDataset.sessionCount || 0}
              />
              <StatCard
                icon={<CoffeeIcon />}
                iconBg="#eff6ff"
                iconColor={colors.primary}
                label="Idle Time"
                value={formatDuration(summaryDataset.idleTime)}
              />
              <StatCard
                icon={<LeaveIcon />}
                iconBg="#eff6ff"
                iconColor={colors.primary}
                label="Leaves"
                value="2/10"
              />
              <StatCard
                icon={<AssetIcon />}
                iconBg="#eff6ff"
                iconColor={colors.primary}
                label="Total Assets"
                value="4"
              />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm py-3 grid grid-cols-2 lg:grid-cols-4 gap-y-3 lg:divide-x divide-gray-300 dark:divide-gray-600">
              <div className="flex items-center gap-2 px-5 text-sm font-semibold lg:justify-center" style={{ color: colors.primary }}>
                <BulbIcon />
                Insights
              </div>

              <div className="flex items-center gap-2.5 px-5 lg:justify-center">
                <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <span style={{ color: colors.primary }}>
                    <AwardIcon />
                  </span>
                </div>
                <div className="text-[13px] leading-tight">
                  <p className="text-gray-500 dark:text-gray-400">Applications used</p>
                  <p className="font-semibold text-gray-900 dark:text-white">{summaryDataset.uniqueApplications || 0} apps</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 px-5 lg:justify-center">
                <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                  <span style={{ color: colors.primary }}>
                    <TrendIcon />
                  </span>
                </div>
                <div className="text-[13px] leading-tight">
                  <p className="text-gray-500 dark:text-gray-400">Time focused</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {(summaryDataset.activePercentage || 0).toFixed(0)}% of tracked time
                  </p>
                </div>
              </div>

              {summaryDataset.top5AppsDisplay?.[0] && (
                <div className="flex items-center gap-2.5 px-5 lg:justify-center">
                  <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                    <span style={{ color: colors.primary }}>
                      <StarIcon />
                    </span>
                  </div>
                  <div className="text-[13px] leading-tight">
                    <p className="text-gray-500 dark:text-gray-400">Most used app</p>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {appDisplayName(summaryDataset.top5AppsDisplay[0].applicationName)}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                <div className="flex items-center gap-1.5 mb-3">
                  <h3 className="font-semibold text-base text-gray-900 dark:text-white">Time distribution</h3>
                  <span className="text-gray-400">
                    <InfoIcon />
                  </span>
                </div>
                <div className="flex items-center gap-5">
                  <div className="relative shrink-0" style={{ width: svgSize, height: svgSize }}>
                    <svg width={svgSize} height={svgSize} className="-rotate-90">
                      <circle
                        cx={svgSize / 2}
                        cy={svgSize / 2}
                        r={radius}
                        fill="none"
                        className="stroke-gray-100 dark:stroke-gray-700"
                        strokeWidth={strokeWidth}
                      />
                      {validSlices.map((s, i) => {
                        const sliceLength = (s.percent / 100) * circumference;
                        const dashLength = Math.max(0, sliceLength - (validSlices.length > 1 ? gapInPixels : 0));
                        const element = (
                          <circle
                            key={i}
                            cx={svgSize / 2}
                            cy={svgSize / 2}
                            r={radius}
                            fill="none"
                            stroke={s.color}
                            strokeWidth={strokeWidth}
                            strokeDasharray={`${dashLength} ${circumference}`}
                            strokeDashoffset={-currentOffset}
                          />
                        );
                        currentOffset += sliceLength;
                        return element;
                      })}
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-[11px] text-gray-400 leading-none">Total</span>
                      <span className="text-base font-bold text-gray-900 dark:text-white mt-1 leading-none">
                        {formatDuration(summaryDataset.totalTime)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 space-y-2">
                    {donutSlices.map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-[13px]">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          <span className="text-gray-600 dark:text-gray-300 font-medium truncate">{s.label}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-semibold text-gray-900 dark:text-white">{s.value}</span>
                          <span className="text-gray-400 w-9 text-right">{s.percent.toFixed(0)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {summaryDataset.activePercentage > 0 && (
                  <div className="mt-auto pt-3">
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 flex items-center gap-2 text-[12.5px] font-small text-blue-700 dark:text-blue-400">
                      <AwardIcon />
                      Great focus! {(summaryDataset.activePercentage || 0).toFixed(0)}% of tracked time was spent on focused work.
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-base text-gray-900 dark:text-white">Top Applications</h3>
                </div>
                {!summaryDataset.top5AppsDisplay || summaryDataset.top5AppsDisplay.length === 0 ? (
                  <p className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                    No application data available.
                  </p>
                ) : (
                  <div className="space-y-2.5 max-h-[190px] overflow-y-auto pr-2">
                    {summaryDataset.top5AppsDisplay.map((app: any, i: number) => {
                      const logo = getAppLogoStyle(app.applicationName);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-[12px] font-medium text-gray-400 w-3">{i + 1}</span>
                          <div
                            className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0"
                            style={{ backgroundColor: logo.bg, color: logo.fg }}
                          >
                            {logo.mark}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-[13px] mb-1">
                              <span className="text-gray-800 font-medium dark:text-gray-200 truncate">
                                {appDisplayName(app.applicationName)}
                              </span>
                              <div className="flex items-center gap-3 shrink-0 ml-2">
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {app.usagePercentage.toFixed(1)}%
                                </span>
                                <span className="text-gray-400 w-16 text-right whitespace-nowrap">
                                  {formatDuration(app.duration)}
                                </span>
                              </div>
                            </div>
                            <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${app.usagePercentage}%`, backgroundColor: colors.primary }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                <div className="flex items-center gap-1.5 mb-3">
                  <h3 className="font-semibold text-base text-gray-900 dark:text-white">Top URLs</h3>
                  <GlobeIcon />
                </div>
                {!summaryDataset.top5UrlsDisplay || summaryDataset.top5UrlsDisplay.length === 0 ? (
                  <p className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                    No url data available right now.
                  </p>
                ) : (
                  <div className="space-y-2.5 max-h-[190px] overflow-y-auto pr-2">
                    {summaryDataset.top5UrlsDisplay.map((urlItem: any, i: number) => {
                      const logo = getUrlLogoStyle(urlItem.domain);
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-[12px] font-medium text-gray-400 w-3">{i + 1}</span>
                          <div
                            className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0"
                            style={{ backgroundColor: logo.bg, color: logo.fg }}
                          >
                            {logo.mark}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between text-[13px] mb-1">
                              <a
                                href={`https://${urlItem.domain}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group flex items-center gap-1 min-w-0 text-gray-800 font-medium dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title={urlItem.domain}
                              >
                                <span className="truncate">{urlItem.domain}</span>
                                <ExternalLinkIcon />
                              </a>
                              <div className="flex items-center gap-3 shrink-0 ml-2">
                                <span className="font-semibold text-gray-900 dark:text-white">
                                  {urlItem.usagePercentage.toFixed(1)}%
                                </span>
                                <span className="text-gray-400 w-16 text-right whitespace-nowrap">
                                  {formatDuration(urlItem.duration)}
                                </span>
                              </div>
                            </div>
                            <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full"
                                style={{ width: `${urlItem.usagePercentage}%`, backgroundColor: colors.primary }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {summaryDataset.days && summaryDataset.days.length > 0 && (
              <Timeline
                days={summaryDataset.days}
                subtitle="Active, idle and offline periods plotted on a shared clock, with holidays and leaves."
                formatDuration={formatDuration}
                email={employeeEmail}
              />
            )}
          </div>
        );
      }

      case "sessions": {
        const disabledBanner = features?.appTrackingEnabled?.userValue === false
          ? <TrackingDisabledBanner message="App tracking is disabled, for the user enable it from user settings." />
          : undefined;

        if (!sessionDataset || sessionDataset.length === 0) {
          return (
            <div className="space-y-4">
              {disabledBanner && <div className="flex px-4 pt-4">{disabledBanner}</div>}
              <p className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No session data available.</p>
            </div>
          );
        }
        return (
          <Sessions
            sessions={sessionDataset.map((session, index) => ({
              id: session.id || index + 1,
              date: session.startTime,
              startTime: session.startTime,
              endTime: session.endTime,
              duration: session.duration,
              productivity: session.sessionType,
              application: session.application,
              status: session.status || "IDLE",
            }))}
            totalPages={sessionsPagination.totalPages}
            totalElements={sessionsPagination.totalElements}
            currentPage={sessionsPagination.currentPage}
            pageSize={sessionsPagination.pageSize}
            onPageChange={handleSessionsPageChange}
            disabledBanner={disabledBanner}
          />
        );
      }

      case "urlSessions": {
        const disabledBanner = features?.urlTrackingEnabled?.userValue === false
          ? <TrackingDisabledBanner message="URL tracking is disabled for the user, enable it from user settings." />
          : undefined;

        if (!urlSessionDataset || urlSessionDataset.length === 0) {
          return (
            <div className="space-y-4">
              {disabledBanner && <div className="flex px-4 pt-4">{disabledBanner}</div>}
              <p className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No URL session data available.</p>
            </div>
          );
        }
        return (
          <UrlSessions
            urlSessions={urlSessionDataset.map((urlSession, index) => ({
              id: urlSession.id || index + 1,
              url: urlSession.url,
              domain: urlSession.domain,
              visits: 1,
              duration: urlSession.duration,
              category: urlSession.browser,
              browser: urlSession.browser,
              startTime: urlSession.startTime,
              endTime: urlSession.endTime,
              lastVisit: urlSession.startTime,
              title: urlSession.title,
            }))}
            totalPages={urlSessionsPagination.totalPages}
            totalElements={urlSessionsPagination.totalElements}
            currentPage={urlSessionsPagination.currentPage}
            pageSize={urlSessionsPagination.pageSize}
            onPageChange={handleUrlSessionsPageChange}
            disabledBanner={disabledBanner}
          />
        );
      }

      case "screenshots": {
        const disabledBanner = features?.screenshotEnabled?.userValue === false
          ? <TrackingDisabledBanner message="Screenshot tracking is disabled for the user, enable it from user settings." />
          : undefined;

        if (!screenshotsDataset || screenshotsDataset.length === 0) {
          return (
            <div className="space-y-4">
              {disabledBanner && <div className="flex px-4 pt-4">{disabledBanner}</div>}
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No screenshots available
              </div>
            </div>
          );
        }

        const screenshotItems: MediaItem[] = screenshotsDataset.map((s) => ({
          url: s.imageUrl,
          name: s.filename || s.imageUrl,
          mimeType: "image/jpeg",
        }));

        return (
          <div className="space-y-4 px-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 shrink-0">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Showing{" "}
                  {screenshotsPagination.totalElements > 0 ? (screenshotsPagination.currentPage - 1) * screenshotsPagination.pageSize + 1 : 0}{" "}
                  -{" "}
                  {Math.min(
                    screenshotsPagination.currentPage *
                    screenshotsPagination.pageSize,
                    screenshotsPagination.totalElements,
                  )}{" "}
                  of {screenshotsPagination.totalElements} screenshots
                </span>
                <select
                  value={screenshotsPagination.pageSize}
                  onChange={(e) =>
                    handleScreenshotsPageChange(1, Number(e.target.value))
                  }
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                >
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              {disabledBanner}

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() =>
                    handleScreenshotsPageChange(
                      screenshotsPagination.currentPage - 1,
                      screenshotsPagination.pageSize,
                    )
                  }
                  disabled={screenshotsPagination.currentPage <= 1}
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Page {screenshotsPagination.currentPage} of{" "}
                  {Math.max(1, screenshotsPagination.totalPages)}
                </span>
                <button
                  onClick={() =>
                    handleScreenshotsPageChange(
                      screenshotsPagination.currentPage + 1,
                      screenshotsPagination.pageSize,
                    )
                  }
                  disabled={
                    screenshotsPagination.currentPage >=
                    screenshotsPagination.totalPages
                  }
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {screenshotsDataset.map((screenshot, index) => {
                let dateStr = "Invalid Date";
                let timeStr = "";
                if (screenshot.timestamp) {
                  const utcTimestamp = screenshot.timestamp.endsWith("Z")
                    ? screenshot.timestamp
                    : screenshot.timestamp + "Z";
                  const dateObj = new Date(utcTimestamp);
                  if (!isNaN(dateObj.getTime())) {
                    dateStr = dateObj.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    });
                    timeStr = dateObj.toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    });
                  }
                }
                return (
                  <div
                    key={index}
                    className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <button
                      type="button"
                      onClick={() => openScreenshotPreview(screenshotItems, index)}
                      className="relative w-full bg-gray-100 dark:bg-gray-700 h-48 overflow-hidden group cursor-zoom-in focus:outline-none block"
                      aria-label={`Preview screenshot ${screenshot.filename || index + 1}`}
                    >
                      {screenshot.imageUrl ? (
                        <img
                          src={getImageUrl(screenshot.imageUrl)}
                          alt={`Screenshot ${screenshot.filename}`}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.opacity = "0.2";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200 flex items-center justify-center">
                        <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                    </button>

                    <div className="p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {dateStr} {timeStr}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                          {(screenshot.fileSize / 1000).toFixed(2)} KB
                        </span>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => openScreenshotPreview(screenshotItems, index)}
                          style={{ backgroundColor: colors.primary }}
                          className="flex-1 px-3 py-2 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                        >
                          Preview
                        </button>
                        <a
                          href={screenshot.imageUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                          title="Open in new tab"
                        >
                          ↗
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case "deviceStatus": {
        const disabledBanner = features?.deviceTrackingEnabled?.userValue === false
          ? <TrackingDisabledBanner message="Device tracking is disabled for the user, enable it from user settings." />
          : undefined;

        if (!deviceStatusDataset) {
          return (
            <div className="space-y-4">
              {disabledBanner && <div className="flex px-4 pt-4">{disabledBanner}</div>}
              <p className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No device status data available.</p>
            </div>
          );
        }
        return (
          <DeviceStatus
            screenLockEnabled={deviceStatusDataset?.screenLockEnabled}
            screenLockTimeout={deviceStatusDataset?.screenLockTimeout}
            diskEncrypted={deviceStatusDataset?.diskEncrypted}
            lastUpdated={deviceStatusDataset?.lastUpdated}
            antivirusEnabled={deviceStatusDataset?.antivirusEnabled}
            windowsAutoUpdateEnabled={deviceStatusDataset?.windowsAutoUpdateEnabled}
            windowsUpdatesPending={deviceStatusDataset?.windowsUpdatesPending}
            windowsLastUpdateDate={deviceStatusDataset?.windowsLastUpdateDate}
            disabledBanner={disabledBanner}
            osName={deviceStatusDataset.osName}
            osVersion={deviceStatusDataset.osVersion}
          />
        );
      }

      case "faceRecognition": {
        const disabledBanner = features?.presenceTrackingEnabled?.userValue === false
          ? <TrackingDisabledBanner message="Face ID tracking is disabled for the user, enable it from user settings." />
          : undefined;

        if (!faceDetectionDataset || faceDetectionDataset.contents.length === 0) {
          return (
            <div className="space-y-4">
              {disabledBanner && <div className="flex px-4 pt-4">{disabledBanner}</div>}
              <p className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">No face detection data available.</p>
            </div>
          );
        }
        return (
          <FaceDetection
            data={faceDetectionDataset?.contents || []}
            disabledBanner={disabledBanner}
          />
        );
      }

      case "liveStream": {
        const disabledBanner = features?.liveScreenEnabled?.userValue === false
          ? <TrackingDisabledBanner message="Live Screen tracking is disabled for the user, enable it from user settings." />
          : undefined;

        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="mb-3">
              <div className="flex items-center w-full mb-0.5">
                <div className="flex items-center gap-2 shrink-0">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Live Screen Activity</h3>
                </div>
                {disabledBanner && (
                  <div className="flex-1 flex justify-center px-4 min-w-0">
                    {disabledBanner}
                  </div>
                )}
              </div>
              {/* <p className="text-xs text-gray-500 dark:text-gray-400">
                Real-time view of {employeeEmail || "the employee"}'s screen.
              </p> */}
            </div>
            <EmployeeLiveMonitor
              targetUserId={employeeId.toString()}
              managerJwt={managerJwt}
            />
          </div>
        );
      }

      default:
        return null;
    }
  };

  if (hideNavigation) {
    return (
      <div>
        {renderContent()}
        <MediaPreview {...previewProps} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-visible">
        <div className="flex items-stretch">
          <nav className="flex flex-1 overflow-x-auto">
            {menuItems.map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveView(item.key)}
                className={`relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0 ${activeView === item.key
                  ? "text-blue-600 dark:text-blue-400"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 "
                  }`}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.key === "liveStream" && (
                  <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                )}
                {activeView === item.key && (
                  <div className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-blue-600 dark:bg-blue-400" />
                )}
              </button>
            ))}
          </nav>

          {activeView !== "userSettings" && activeView !== "liveStream" && (
            <div className="px-3 flex-shrink-0 border-gray-200 dark:border-gray-700 self-stretch flex items-center">
              <CommonListingFilter
                className="[&>button]:h-8"
                fields={workSummaryFilterFields}
                panelTitle="Filter by Date Range"
                panelAlign="right"
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                onReset={handleResetFilters}
                onClose={handleCloseFilter}
              />
            </div>
          )}
        </div>
      </div>

      {renderContent()}
      <MediaPreview {...previewProps} />
    </div>
  );
};

export default WorkSummary;